/**
 * Hooks for adventure category management
 */

import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adventureCategoriesApi } from '@api/endpoints/adventureCategories'
import type {
  AdventureCategoryDto,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '@api/types/adventureCategory'

// ===================
// QUERY HOOKS
// ===================

/**
 * Fetch all adventure categories
 */
export function useAdventureCategories(includeInactive: boolean = false) {
  return useQuery({
    queryKey: ['adventure-categories', { includeInactive }],
    queryFn: () => adventureCategoriesApi.getCategories({ includeInactive }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/** Label shown for a category id before the rows arrive, or for an id no row knows. */
export const FALLBACK_CATEGORY_LABEL = 'Adventure'

/**
 * Every category row (inactive ones too, so a retired category still names its old adventures
 * and stats), in sortOrder, with an id lookup. Lists a user picks from take `active`; this is the
 * only place the portal learns what categories exist, so a new row needs no client change.
 */
export function useCategoryLookup() {
  const { data, isLoading } = useAdventureCategories(true)

  return useMemo(() => {
    const all: AdventureCategoryDto[] = [...(data ?? [])].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
    )
    const byId = new Map(all.map((c) => [c.id, c]))
    return {
      all,
      active: all.filter((c) => c.isActive),
      byId,
      isLoading,
      label: (id: number | null | undefined) =>
        (id != null && byId.get(id)?.displayName) || FALLBACK_CATEGORY_LABEL,
    }
  }, [data, isLoading])
}

/**
 * Fetch a single category with full details
 */
export function useAdventureCategory(id: number | undefined) {
  return useQuery({
    queryKey: ['adventure-category', id],
    queryFn: () => adventureCategoriesApi.getCategory(id!),
    enabled: id !== undefined,
  })
}

/**
 * Fetch all user flags for dropdown
 */
export function useUserFlags() {
  return useQuery({
    queryKey: ['user-flags'],
    queryFn: () => adventureCategoriesApi.getUserFlags(),
    staleTime: 30 * 60 * 1000, // 30 minutes - these rarely change
  })
}

/**
 * Fetch all criteria types for multi-selects
 */
export function useCriteriaTypes() {
  return useQuery({
    queryKey: ['criteria-types'],
    queryFn: () => adventureCategoriesApi.getCriteriaTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes - these rarely change
  })
}

/**
 * Fetch all node types for multi-select
 */
export function useNodeTypes() {
  return useQuery({
    queryKey: ['node-types'],
    queryFn: () => adventureCategoriesApi.getNodeTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes - these rarely change
  })
}

// ===================
// MUTATION HOOKS
// ===================

/**
 * Create a new category
 */
export function useCreateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCategoryRequest) =>
      adventureCategoriesApi.createCategory(data),
    onSuccess: () => {
      // Invalidate categories list
      queryClient.invalidateQueries({ queryKey: ['adventure-categories'] })
    },
  })
}

/**
 * Update an existing category
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCategoryRequest }) =>
      adventureCategoriesApi.updateCategory(id, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific category
      queryClient.invalidateQueries({ queryKey: ['adventure-category', variables.id] })
      // Invalidate categories list
      queryClient.invalidateQueries({ queryKey: ['adventure-categories'] })
    },
  })
}

/**
 * Delete a category
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) =>
      adventureCategoriesApi.deleteCategory(id),
    onSuccess: (_, id) => {
      // Invalidate the specific category
      queryClient.invalidateQueries({ queryKey: ['adventure-category', id] })
      // Invalidate categories list
      queryClient.invalidateQueries({ queryKey: ['adventure-categories'] })
    },
  })
}
